import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RouteComponentProps, useHistory } from 'react-router';
import { initVideoCall, leaveFromVideoCall, useAgoraClient } from '../../api/agora';
import { useDispatch, useSelector } from 'react-redux';
import { IonContent, IonLoading, IonPage, isPlatform, useIonViewWillEnter, useIonViewWillLeave } from '@ionic/react';
import { psychologistSelectorById } from '../../redux/specialists/selector';
import { setIsActive } from '../../redux/tabBar/reducer';
import { ConsultationType } from '../types';
import AgoraRTC, {
  IAgoraRTCClient,
  IAgoraRTCRemoteUser,
  ICameraVideoTrack,
  ILocalVideoTrack,
  IMicrophoneAudioTrack,
  IRemoteAudioTrack,
  IRemoteVideoTrack,
} from 'agora-rtc-sdk-ng';
import {
  AudioStatus,
  getConsultation,
  userInfoUpdatedEventHandler,
  userJoinedOrLeftEventHandler,
  userPublishedEventHandler,
  VideoStatus,
} from './utils';
import { call, desktopOutline, micOffOutline, micOutline, videocamOffOutline, videocamOutline } from 'ionicons/icons';
import VideoCallControlPanel from './VideoCallControlPanel/VideoCallControlPanel';
import { MediaPlayer, NotSupportModal, RoundButton, VideoCallInfo } from '@mindly/ui-components';
import { notifyAboutJoinToTheVideoChat } from '../../api/notifications';
import {
  LocalMediaPlayerContainer,
  MainMediaPlayerContainer,
  MediaPlayersContainer,
  MediaPlayerSidebarItem,
  MediaPlayersSideBar,
  StyledVideoCallContainer,
} from './VideoCall.style';
import { initScreenShareVideo, leaveScreenShareVideo } from '../../api/agora/videoCalls/screenShareVideo';
import { config as agoraClientConfig } from '../../api/agora/client';
import { PrivateRoutes, PublicRoutes } from '../../routes/navigation';
import { consultationTimeToFormatSecondsWithUndefinedCase } from '../SchedulePage/utils';
import { DateTime } from 'luxon';
import useLangs from '../../context/LangsContext';
import { useIntl } from 'react-intl';
import { userSelector } from '../../redux/user/selector';
import { ratedSpecialistSelector } from '../../redux/specialistReviews/selector';
import { consultationsBySpecialistIDSelector } from '../../redux/consultations/selector';
import SwitchDeviceModal from './SwitchDeviceModal/SwitchDeviceModal';
import useToast from '../../context/ToastContext/ToastContext';
import { fetchSpecialistsList } from '../../redux/specialists/thunk';
import { setReviewData } from '../../redux/needReview/reducer';
import { useBeforeUnload } from '../../hooks/usePrompt';
import { setConsultationReview } from '../../redux/consultationReviews/consultationsReviews.reducer';
import { useAuthFlow } from '../../shared';
import { selectRatedConsultations } from '../../redux/consultationReviews/consultationsReviews.selector';
import { ISpecialist } from '../../redux/specialists/types';
import { FirebaseAnalytics } from '@capacitor-community/firebase-analytics';

type VideoCallPageParams = RouteComponentProps<{
  consultationID: string;
  psychologistID: string;
}>;

let clientScreen: IAgoraRTCClient | null = null;

const VideoCall: React.FC<VideoCallPageParams> = ({ match }) => {
  const client = useAgoraClient();

  const history = useHistory();
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { locale } = useLangs();
  const intl = useIntl();
  const { authUser } = useAuthFlow();
  const { psychologistID, consultationID } = match.params;

  const specialist = useSelector(psychologistSelectorById(psychologistID));
  const currentUser = useSelector(userSelector);
  const ratedSpecialist = useSelector(ratedSpecialistSelector);
  const ratedConsultations = useSelector(selectRatedConsultations);
  const consultations = useSelector(consultationsBySpecialistIDSelector(specialist?.id!));
  const [isShowNotSupportModal, setIsShowNotSupportModal] = useState(false);
  const [consultation, setConsultation] = useState<ConsultationType | null>(null);
  const [showLoader, setShowLoader] = useState(true);

  const [availableDevices, setAvailableDevices] = useState<MediaDeviceInfo[]>([]);
  const [showSwitchDevice, setShowSwitchDevice] = useState<'audio' | 'video' | ''>('');

  const [localAudioEnabled, setLocalAudioEnabled] = useState(true);
  const [localVideoEnabled, setLocalVideoEnabled] = useState(true);

  const [remoteAudioStatus, setRemoteAudioStatus] = useState<AudioStatus | null>(null);
  const [remoteVideoStatus, setRemoteVideoStatus] = useState<VideoStatus | null>(null);

  const [, updateState] = useState<object>();
  const forceUpdate = useCallback(() => updateState({}), []);

  const localDownload = useRef<number | null>(null);
  const localUpload = useRef<number | null>(null);

  const remoteDownload = useRef<number | null>(null);
  const remoteUpload = useRef<number | null>(null);

  const [participants, setParticipants] = useState<IAgoraRTCRemoteUser[]>([]);

  const [isShowingControlPanel, setIsShowingControlPanel] = useState(false);
  const [isShowMicRecommendations, setIsShowMicRecommendations] = useState(false);

  const [isScreenShareStarted, setIsScreenShareStarted] = useState(false);
  const [isScreenShareLoad, setIsScreenShareLoad] = useState(false);

  const videoContainer = useRef<HTMLDivElement | null>(null);
  const mainContainer = useRef<HTMLDivElement | null>(null);

  const localCameraTrack = useRef<ICameraVideoTrack | null>(null);
  const localAudioTrack = useRef<IMicrophoneAudioTrack | null>(null);

  const psychologistNameWithDot = useMemo(() => {
    if (specialist?.first_name && specialist?.last_name)
      return `${specialist?.first_name} ${specialist?.last_name[0]}.`;

    // TODO: Add translation for this case in i18n
    return 'Психолог';
  }, [specialist]);

  const initSession = useCallback(
    async (ongoingConsultation: ConsultationType) => {
      const videoCallInformation = await initVideoCall({
        client,
        consultationID: ongoingConsultation.id,
        channelID: ongoingConsultation.videochat_url,
        userID: ongoingConsultation.user_id,
      });

      if (videoCallInformation?.camerasList?.length && videoCallInformation?.cameraTrack)
        setAvailableDevices(videoCallInformation.camerasList);

      localAudioTrack.current = videoCallInformation.microphoneTrack;
      localCameraTrack.current = videoCallInformation.cameraTrack ?? null;
    },
    [client?.uid]
  );

  const openReviewModalController = useCallback(
    (currentConsultation: ConsultationType) => {
      const consultationTimeSeconds = consultationTimeToFormatSecondsWithUndefinedCase(
        currentConsultation?.consultation_time
      );

      return (
        consultationTimeSeconds &&
        DateTime.fromSeconds(consultationTimeSeconds).plus({ minutes: 40 }).toSeconds() < DateTime.now().toSeconds() &&
        !ratedSpecialist?.find((review) => review === specialist?.id) &&
        (consultations?.length ?? 0) >= 2
      );
    },
    [specialist?.id, ratedSpecialist, consultations?.length]
  );

  const hasShowNeedReviewFirstConsultation = useMemo(() => {
    const consultationTimeSeconds = consultationTimeToFormatSecondsWithUndefinedCase(consultation?.consultation_time);

    return (
      consultationTimeSeconds &&
      DateTime.fromSeconds(consultationTimeSeconds).plus({ minutes: 40 }).toSeconds() &&
      !ratedConsultations.find((c) => c.consultation_id === consultation?.id) &&
      (consultations?.length ?? 0) === 1
    );
  }, [consultations, consultation?.consultation_time, ratedConsultations]);

  const handleSetConsultationReview = useCallback(
    (currentSpecialist: ISpecialist) => {
      if (!hasShowNeedReviewFirstConsultation) return;

      dispatch(
        setConsultationReview({
          consultation_id: consultation?.id,
          specialist_id: currentSpecialist?.id,
          client_id: currentUser?.id,
          client_name: `${currentUser?.display_name} ${currentUser?.last_name ?? ''} `,
          client_avatar: currentUser?.avatar ?? null,
          currentSpecialist,
        })
      );
    },
    [consultation, currentUser, hasShowNeedReviewFirstConsultation]
  );

  const leaveSession = useCallback(
    (trigger: 'leave-session' | 'close-browser-tab' = 'leave-session') =>
      async () => {
        try {
          setLocalAudioEnabled(true);
          setLocalVideoEnabled(true);

          await leaveFromVideoCall(client, localCameraTrack.current, localAudioTrack.current);
          videoContainer.current = null;
          client.removeAllListeners();

          clientScreen && (await leaveScreenShareVideo(clientScreen));
          clientScreen = null;

          if (!consultation?.consultation_time) return;
          if (openReviewModalController(consultation)) {
            if (!specialist) return;
            dispatch(setReviewData(specialist));
          }

          if (hasShowNeedReviewFirstConsultation) {
            if (!specialist) return;
            FirebaseAnalytics.logEvent({
              name: 'trigger_first_trial_consultation_review_modal',
              params: {
                consultation_id: consultation?.id,
                specialist_id: specialist?.id,
                client_id: currentUser?.id,
                trigger,
              },
            });
            handleSetConsultationReview(specialist);
          }

          history.replace(PrivateRoutes.CONSULTATION);
        } catch (error) {
          console.error(error);
          history.replace(PrivateRoutes.CONSULTATION);
        }
      },
    [
      specialist,
      client,
      clientScreen,
      localCameraTrack.current,
      localAudioTrack.current,
      consultation?.consultation_time,
      history,
      hasShowNeedReviewFirstConsultation,
      handleSetConsultationReview,
    ]
  );

  useBeforeUnload(leaveSession('close-browser-tab'), !!hasShowNeedReviewFirstConsultation);

  const applyListeners = useCallback(() => {
    client.on('network-quality', (quality) => {
      if (localDownload.current !== quality?.downlinkNetworkQuality ?? 0) {
        localDownload.current = quality?.downlinkNetworkQuality ?? 0;
        forceUpdate();
      }

      if (localUpload.current !== quality?.uplinkNetworkQuality ?? 0) {
        localUpload.current = quality?.uplinkNetworkQuality ?? 0;
        forceUpdate();
      }

      const participantsNetworkQuality = client.getRemoteNetworkQuality();
      const participantNetworkQuality =
        participantsNetworkQuality[
          Object.keys(participantsNetworkQuality).find((el) => el === client?.remoteUsers[0]?.uid.toString()) ?? ''
        ];

      if ((participantNetworkQuality?.uplinkNetworkQuality ?? null) !== remoteUpload.current) {
        remoteUpload.current = participantNetworkQuality?.uplinkNetworkQuality ?? null;
        forceUpdate();
      }

      if ((participantNetworkQuality?.downlinkNetworkQuality ?? null) !== remoteDownload.current) {
        remoteDownload.current = participantNetworkQuality?.downlinkNetworkQuality ?? null;
        forceUpdate();
      }
    });

    client.on('user-info-updated', userInfoUpdatedEventHandler(setRemoteVideoStatus, setRemoteAudioStatus));

    client.on('user-published', userPublishedEventHandler(client, videoContainer, setParticipants));

    client.on('user-joined', userJoinedOrLeftEventHandler(setParticipants, 'user-joined'));

    client.on('user-left', userJoinedOrLeftEventHandler(setParticipants, 'user-left'));
  }, [participants.length]);

  const participantsWithoutScreenSharing = useMemo(
    () => participants.filter((i) => !i.uid.toString().includes('_screen')),
    [participants.length]
  );

  const participantsScreenSharing = useMemo(
    () => participants.filter((i) => i.uid.toString().includes('_screen')),
    [participants.length]
  );

  const screenSharingInit = useCallback(async () => {
    if (!isPlatform('desktop')) {
      showToast({
        text: intl.formatMessage({
          id: 'videoCall.mobile.not.share',
          defaultMessage: "Поки можливість поділитися своїм екраном доступна тільки на комп'ютері.",
        }),
      });
      return;
    }

    if (!consultation?.id) return;

    if (participantsScreenSharing.length) {
      showToast({
        text: intl.formatMessage({
          id: 'videoCall.warning.once.screen',
          defaultMessage: 'Лише одна людина може ділитися своїм екраном одночасно.',
        }),
      });
      return;
    }

    clientScreen = AgoraRTC.createClient(agoraClientConfig);

    try {
      await initScreenShareVideo({
        client: clientScreen,
        consultationID: consultationID,
        channelID: consultation.videochat_url,
        userID: consultation.user_id,
        loading: setIsScreenShareLoad,
      });
      setIsScreenShareStarted(true);
    } catch (error) {
      showToast({ error });
      history.replace(PrivateRoutes.CONSULTATION);
      clientScreen = null;
    }
  }, [consultationID, consultation, clientScreen, participantsScreenSharing.length, intl]);

  const leaveScreenSharing = useCallback(async () => {
    if (!clientScreen) return;

    await leaveScreenShareVideo(clientScreen);
    setIsScreenShareStarted(false);
    clientScreen = null;
  }, [clientScreen]);

  const onClickVideoButton = useCallback(async () => {
    setShowSwitchDevice('video');

    const camerasList = await AgoraRTC.getCameras().catch(() => []);

    const filteredDevice = camerasList.filter((el) => el.deviceId !== 'default');

    if (filteredDevice.length !== availableDevices.length && localCameraTrack.current) {
      setAvailableDevices(filteredDevice);
    }
  }, []);

  const onClickAudioButton = useCallback(async () => {
    setLocalAudioEnabled(!localAudioEnabled);
  }, [localAudioEnabled, localAudioTrack?.current]);

  const onClickVideoDeviceHandler = useCallback(
    async (deviceID: string) => {
      const isNeedToBeSwitched =
        !localVideoEnabled || localCameraTrack?.current?.getMediaStreamTrack().getSettings().deviceId !== deviceID;

      if (isNeedToBeSwitched) {
        await localCameraTrack.current?.setDevice(deviceID);
        setShowSwitchDevice('');
        setLocalVideoEnabled(true);
      } else {
        setShowSwitchDevice('');
      }
    },
    [localCameraTrack, localVideoEnabled]
  );

  const onDisableVideoDeviceHandler = useCallback(() => {
    setLocalVideoEnabled(false);
    setShowSwitchDevice('');
  }, []);

  useIonViewWillEnter(async () => {
    try {
      const userID = authUser?.uid;
      if (!userID) return;
      setShowLoader(true);
      !specialist && (await dispatch(fetchSpecialistsList(locale)));
      const consultationData = await getConsultation(consultationID);

      if (!consultationData?.id) throw new Error('consultation_not_found');
      await applyListeners();
      await initSession(consultationData);
      notifyAboutJoinToTheVideoChat(consultationData.user_id, consultationData.psychologist_id);

      setConsultation(consultationData);
      setIsShowMicRecommendations(true);
    } catch (error: any) {
      if (error?.message?.includes('NOT_SUPPORTED')) return setIsShowNotSupportModal(true);

      showToast({ error });
      history.replace(PrivateRoutes.CONSULTATION);
    } finally {
      setShowLoader(false);
    }
  });

  useIonViewWillEnter(() => {
    if (!currentUser?.id) {
      history.push(PublicRoutes.AUTH);
      return;
    }

    dispatch(setIsActive(false));
  });

  useIonViewWillEnter(() => {
    AgoraRTC.onMicrophoneChanged = async () => {
      const audioDevices = await AgoraRTC.getMicrophones();
      if (audioDevices[0].deviceId) localAudioTrack?.current?.setDevice(audioDevices[0].deviceId);
    };
  });

  useIonViewWillLeave(() => {
    dispatch(setIsActive(true));
    leaveSession('leave-session')();
  });

  useEffect(() => {
    if (!mainContainer.current) return;

    mainContainer.current.addEventListener('click', (e) => {
      e.stopPropagation();
      setIsShowingControlPanel((prev) => !prev);
    });

    return () => {
      mainContainer.current?.removeEventListener('click', (e) => {
        e.stopPropagation();
        setIsShowingControlPanel((prev) => !prev);
      });
    };
  }, [mainContainer?.current]);

  useEffect(() => {
    if (!participants.length) {
      setRemoteAudioStatus(null);
      setRemoteVideoStatus(null);
    }
  }, [participants.length]);

  useEffect(() => {
    if (!localCameraTrack) setLocalVideoEnabled(false);
    if (!localAudioTrack) setLocalAudioEnabled(false);
  }, [localCameraTrack, localAudioTrack]);

  useEffect(() => {
    if (!localCameraTrack.current) return;

    (async () => {
      try {
        participantsScreenSharing.length
          ? await localCameraTrack?.current?.setEncoderConfiguration('180p')
          : await localCameraTrack?.current?.setEncoderConfiguration('360p');
      } catch (e) {
        console.log(e);
      }
    })();
  }, [participantsScreenSharing.length, localCameraTrack?.current]);

  useEffect(() => {
    (async () => {
      if (!localCameraTrack.current) return;
      await localCameraTrack.current?.setEnabled(localVideoEnabled);
    })();
  }, [localVideoEnabled, localCameraTrack?.current]);

  useEffect(() => {
    (async () => {
      if (!localAudioTrack.current) return;
      await localAudioTrack.current?.setMuted(!localAudioEnabled);
    })();
  }, [localAudioEnabled, localAudioTrack?.current]);

  useEffect(() => {
    forceUpdate();
  }, [participantsScreenSharing.length, forceUpdate]);

  return (
    <IonPage>
      <IonContent scrollY={false}>
        <StyledVideoCallContainer
          isBlurred={!!showSwitchDevice.length}
          isScreenShare={!!participantsScreenSharing.length}
        >
          {showLoader ? (
            <IonLoading
              mode={'ios'}
              isOpen={showLoader}
              message={intl.formatMessage({
                id: 'general.loading',
                defaultMessage: 'Завантаження',
              })}
            />
          ) : (
            <>
              <MediaPlayersContainer>
                <MainMediaPlayerContainer ref={mainContainer} isScreenShare={!!participantsScreenSharing.length}>
                  {!participantsWithoutScreenSharing.length && !participantsScreenSharing.length ? (
                    <VideoCallInfo
                      userInfo={{
                        name: psychologistNameWithDot,
                        avatar: specialist?.avatar_link ?? '',
                        position: specialist?.specialization ?? '',
                      }}
                      consultationDate={consultationTimeToFormatSecondsWithUndefinedCase(
                        consultation?.consultation_time
                      )}
                      timeZone={DateTime.local().zoneName}
                      locale={locale}
                      translations={{
                        dateAndTime: intl.formatMessage({
                          id: 'videoCall.date.and.time',
                          defaultMessage: 'Дата та час',
                        }),
                        toStartSession: intl.formatMessage({
                          id: 'videoCall.time.title',
                          defaultMessage: 'До початку сеансу залишилось',
                        }),
                        abbreviatedDay: intl.formatMessage({
                          id: 'general.day.short',
                          defaultMessage: 'дн.',
                        }),
                        abbreviatedHour: intl.formatMessage({
                          id: 'general.hour.short',
                          defaultMessage: 'год.',
                        }),
                        abbreviatedMinutes: intl.formatMessage({
                          id: 'general.minutes.short',
                          defaultMessage: 'хв.',
                        }),
                        abbreviatedSeconds: intl.formatMessage({
                          id: 'general.seconds.short',
                          defaultMessage: 'сек.',
                        }),
                      }}
                    />
                  ) : (
                    Array.from(
                      participantsScreenSharing.length ? participantsScreenSharing : participantsWithoutScreenSharing
                    ).map((participant) => (
                      <MediaPlayer<IRemoteVideoTrack, IRemoteAudioTrack>
                        key={participant.uid}
                        fullwidth={true}
                        cameraTrack={participant.videoTrack}
                        audioTrack={participant.audioTrack}
                        isVideoTrackMuted={
                          !participantsScreenSharing.length &&
                          (!participants.length || remoteVideoStatus === 'mute-video')
                        }
                        isAudioTrackMuted={
                          !participantsScreenSharing.length &&
                          (!participants.length || remoteAudioStatus === 'mute-audio')
                        }
                        networkQuality={remoteUpload.current ?? 0}
                        fallbackAvatar={specialist?.avatar_link ?? ''}
                        fallbackAvatarDimensions={120}
                        fallbackName={psychologistNameWithDot}
                        showControls={isShowingControlPanel}
                        isScreenSharing={participantsScreenSharing.length !== 0}
                      />
                    ))
                  )}
                  {participantsScreenSharing.length === 0 && (
                    <LocalMediaPlayerContainer>
                      <MediaPlayer<ILocalVideoTrack, undefined>
                        cameraTrack={localCameraTrack.current as ILocalVideoTrack}
                        audioTrack={undefined}
                        isVideoTrackMuted={!localCameraTrack.current || !localVideoEnabled}
                        isAudioTrackMuted={!localAudioTrack.current || !localAudioEnabled}
                        networkQuality={localUpload.current ?? 0}
                        fallbackAvatar={consultation?.user_avatar}
                        fallbackName={consultation?.user_name}
                        showControls={isShowingControlPanel}
                      />
                    </LocalMediaPlayerContainer>
                  )}
                </MainMediaPlayerContainer>
                {participantsScreenSharing.length !== 0 && (
                  <MediaPlayersSideBar>
                    <MediaPlayerSidebarItem>
                      <MediaPlayer<ILocalVideoTrack, undefined>
                        fullwidth={true}
                        cameraTrack={localCameraTrack.current as ILocalVideoTrack}
                        audioTrack={undefined}
                        isVideoTrackMuted={!localCameraTrack.current || !localVideoEnabled}
                        isAudioTrackMuted={!localAudioTrack.current || !localAudioEnabled}
                        networkQuality={localUpload.current ?? 0}
                        fallbackAvatar={consultation?.user_avatar}
                        fallbackName={consultation?.user_name}
                        showControls={isShowingControlPanel}
                      />
                    </MediaPlayerSidebarItem>
                    {participantsWithoutScreenSharing.map((participant) => (
                      <MediaPlayerSidebarItem key={participant.uid}>
                        <MediaPlayer<IRemoteVideoTrack, IRemoteAudioTrack>
                          fullwidth={true}
                          cameraTrack={participant.videoTrack}
                          audioTrack={participant.audioTrack}
                          isVideoTrackMuted={!participants.length || remoteVideoStatus === 'mute-video'}
                          isAudioTrackMuted={!participants.length || remoteAudioStatus === 'mute-audio'}
                          networkQuality={remoteUpload.current ?? 0}
                          fallbackAvatar={specialist?.avatar_link ?? ''}
                          fallbackName={psychologistNameWithDot}
                          showControls={isShowingControlPanel}
                        />
                      </MediaPlayerSidebarItem>
                    ))}
                  </MediaPlayersSideBar>
                )}
              </MediaPlayersContainer>
              <VideoCallControlPanel
                className={
                  !isShowingControlPanel ? 'control-panel videocall-hidden-down' : 'control-panel videocall-hidden-up'
                }
              >
                <RoundButton
                  disabled={showLoader}
                  buttonType={'reject'}
                  buttonIcon={call}
                  onClickHandler={leaveSession('leave-session')}
                  dimension={65}
                  transformed
                />
                <RoundButton
                  disabled={showLoader}
                  buttonType={!!localCameraTrack.current && localVideoEnabled ? 'transparent' : 'fullFill'}
                  buttonIcon={!!localCameraTrack.current && localVideoEnabled ? videocamOutline : videocamOffOutline}
                  dimension={50}
                  onClickHandler={onClickVideoButton}
                />
                <RoundButton
                  disabled={showLoader}
                  buttonType={!!localAudioTrack.current && localAudioEnabled ? 'transparent' : 'fullFill'}
                  buttonIcon={!!localAudioTrack.current && localAudioEnabled ? micOutline : micOffOutline}
                  dimension={50}
                  onClickHandler={onClickAudioButton}
                />
                <RoundButton
                  disabled={isScreenShareLoad}
                  isLoading={isScreenShareLoad}
                  buttonType={isScreenShareStarted ? 'success' : 'transparent'}
                  buttonIcon={desktopOutline}
                  onClickHandler={() => (isScreenShareStarted ? leaveScreenSharing() : screenSharingInit())}
                  dimension={50}
                />
              </VideoCallControlPanel>
            </>
          )}

          <SwitchDeviceModal
            isOpen={!!showSwitchDevice.length}
            onDidDismiss={() => setShowSwitchDevice('')}
            deviceType={'video'}
            deviceList={availableDevices}
            videoTrack={localCameraTrack.current ?? undefined}
            onClickVideoDeviceHandler={onClickVideoDeviceHandler}
            onDisableVideoDeviceHandler={onDisableVideoDeviceHandler}
          />
          <NotSupportModal
            isOpen={isShowNotSupportModal}
            redText={
              <p
                dangerouslySetInnerHTML={{
                  __html: intl.formatMessage({
                    id: 'videoCall.warning.device.support.title',
                    defaultMessage: 'Ти працюєш на <b>дуже старому телефоні</b>, де немає підтримки відео дзвінків.',
                  }),
                }}
              />
            }
            buttonLink={'https://apps.apple.com/ua/app/google-chrome/id535886823'}
            mainText={
              <p
                dangerouslySetInnerHTML={{
                  __html: intl.formatMessage({
                    id: 'videoCall.warning.device.support.text',
                    defaultMessage:
                      'Ти можеш спробувати встановити <b>браузер Google Chrome</b> та працювати в ньому, але ми не гарантуємо, що відео дзвінки будуть і там працювати.',
                  }),
                }}
              />
            }
            closeModal={() => {
              setIsShowNotSupportModal(false);
              leaveSession('leave-session')();
            }}
            translations={{
              buttonText: intl.formatMessage({
                id: 'understand.btn',
                defaultMessage: 'Зрозуміло',
              }),
              downloadChromeText: intl.formatMessage({
                id: 'general.download.chrome',
                defaultMessage: 'Завантажити Chrome',
              }),
              attention: intl.formatMessage({
                id: 'general.attentions',
                defaultMessage: 'Увага',
              }),
            }}
          />
          <NotSupportModal
            isOpen={isShowMicRecommendations}
            type="primary"
            title={intl.formatMessage({
              id: 'videoCall.recommendation',
              defaultMessage: 'Рекомендація',
            })}
            mainText={
              <>
                <p
                  dangerouslySetInnerHTML={{
                    __html: intl.formatMessage({
                      id: 'videoCall.audio.warning.text1',
                      defaultMessage:
                        'Якщо якість звуку у твого співрозмовника буде погана - <b>багато шуму</b> чи <b>металевий голос</b>',
                    }),
                  }}
                />
                <img src="./videoCallControls.svg" alt={'video controls'} />
                <p
                  dangerouslySetInnerHTML={{
                    __html: intl.formatMessage({
                      id: 'videoCall.audio.warning.text2',
                      defaultMessage: 'Попроси його/її <b>вимкнути і ввімкнути мікрофон</b>, і це вирішиться',
                    }),
                  }}
                />
              </>
            }
            closeModal={() => {
              setIsShowMicRecommendations(false);
            }}
            translations={{
              buttonText: intl.formatMessage({
                id: 'understand.btn',
                defaultMessage: 'Зрозуміло',
              }),
              downloadChromeText: intl.formatMessage({
                id: 'general.download.chrome',
                defaultMessage: 'Завантажити Chrome',
              }),
              attention: intl.formatMessage({
                id: 'general.attentions',
                defaultMessage: 'Увага',
              }),
            }}
          />
        </StyledVideoCallContainer>
      </IonContent>
    </IonPage>
  );
};

export default VideoCall;
